import { useFormikContext } from 'formik';
import { useEffect, useMemo } from 'react';

import { FormPart } from '@modules/shared/components/FormPart';
import { IGapLoanPaidOffRequestInformation } from '@modules/admin/Tools/shared/interfaces';
import { GAP_PRODUCT_TYPE } from '@modules/ContactManager/components/Tabs/components/Deals/components/CheckRequestForm';
import { NumberInputField } from '@shared/fields/NumberInputField';
import { CheckboxField } from '@shared/fields/CheckboxField';
import { TextInputField } from '@shared/fields/TextInputField';
import { SelectDateField } from '@shared/fields/SelectDateField';
import { getOptions } from '@shared/commonFunctions';
import { SelectAutocompleteField } from '@shared/fields/SelectAutocompleteField';
import { getFormattedPayeeOptions, LOAN_PAID_OFF } from '@modules/accounting/utils';
import {
  ICustomCheckRequestFormProps,
  ICustomCheckRequestFormValues,
  IPayeeLender,
  cancellationReasons,
  productTypes,
} from '../interfaces';

export const CustomCheckRequestForm = ({ lenderOptions, lendersError, payeeData = [] }: ICustomCheckRequestFormProps) => {
  const { values, setFieldValue } = useFormikContext<ICustomCheckRequestFormValues>();

  const formattedPayeeOptions = useMemo(() => {
    return getFormattedPayeeOptions(payeeData, values.productType, values.reason);
  }, [payeeData, values.productType, values.reason]);

  const config = useMemo(
    () =>
      [
        {
          name: 'productType',
          label: 'Type',
          options: productTypes,
          component: SelectAutocompleteField,
        },
        { name: 'provider' },
        { name: 'customerName' },
        {
          name: 'lenderCode',
          options: getOptions(lenderOptions),
          isLoading: !lenderOptions && !lendersError,
          component: SelectAutocompleteField,
        },
        {
          name: 'payeeName',
          label: 'Payee',
          options: formattedPayeeOptions,
          component: !values.isPayeeLender ? SelectAutocompleteField : TextInputField,
        },
        { name: 'address' },
        {
          name: 'customerAmountRefund',
          label: 'Customer Amount Refund',
          prefix: '$',
          decimalScale: 2,
          component: NumberInputField,
        },
        {
          name: 'sellerAmountRefund',
          label: 'Seller Amount Refund',
          prefix: '$',
          decimalScale: 2,
          component: NumberInputField,
        },
        {
          name: 'amountRequested',
          label: 'Amount Requested',
          prefix: '$',
          decimalScale: 2,
          component: NumberInputField,
        },
        { name: 'cancelledDate', component: SelectDateField },
        {
          name: 'achRefNumber',
          label: 'ACH REF NUMBER',
          component: NumberInputField,
        },
        { name: 'legacyDealId', label: 'Related Deal' },
        {
          name: 'reason',
          label: 'Reason for Cancellation',
          options: cancellationReasons,
          component: SelectAutocompleteField,
        },
        { name: 'requestComment' },
        { name: 'isPayeeLender', label: 'Enter Payee', component: CheckboxField },
      ].map((field) => ({
        width: '50%',
        minWidth: '100px',
        ...field,
      })),
    [values.isPayeeLender, lenderOptions, lendersError, payeeData, formattedPayeeOptions]
  );

  useEffect(() => {
    if (!values.isPayeeLender) setFieldValue('payeeName', '');
  }, [values.isPayeeLender]);

  const formatAddress = (obj: IPayeeLender | IGapLoanPaidOffRequestInformation) => {
    const address = obj.payeeAddress ?? '';
    const state = obj.payeeState ? ` ${obj.payeeState}` : '';
    const zip = obj.payeeZip ? ` ${obj.payeeZip}` : '';
    return `${address}${state}${zip}`;
  };

  useEffect(() => {
    if (values.payeeName) {
      const payeeObj = formattedPayeeOptions.find((payee) => payee.value === values.payeeName);

      let payeeAddress = '';

      for (const payee of payeeData) {
        if (payee.payeeName === payeeObj?.value) {
          payeeAddress = formatAddress(payee);
        } else {
          const isGapLoanPaidOff =
            values.productType?.toLowerCase() === GAP_PRODUCT_TYPE.toLowerCase() &&
            values.reason?.toLowerCase() === LOAN_PAID_OFF.toLowerCase();

          if (isGapLoanPaidOff) {
            const gapInfo = payee.gapLoanPaidOffRequestInformation;
            if (gapInfo && gapInfo.payeeName === payeeObj?.value) {
              payeeAddress = formatAddress(gapInfo);
              break;
            }
          }
        }
      }
      setFieldValue('address', payeeAddress);
    }
  }, [values.payeeName, formattedPayeeOptions, payeeData, setFieldValue]);

  return <FormPart config={config} />;
};
